import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import ROUTES, { YEAR_QUERY_PARAM } from '../routes/routeConstants';
import {
  ScenarioControllerApi,
  YearPlanControllerApi,
  TreatmentPlanTemplateControllerApi,
  PatientControllerApi,
  PatientRoomPropertyControllerApi
} from '../api/generated';
import type {
  YearPlanResponse,
  TreatmentPlanTemplateResponse,
  TreatmentPlanDto
} from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import {
  collectRenamedTreatmentPlans,
  hasTreatmentPlanOrderChanged,
  type RenamedTreatmentPlan
} from '../services/changeSummaryService';
import {
  moveTreatmentPlanRow,
  type RowMoveDirection
} from '../services/treatmentPlanRowOrderService';
import { normalizeTreatmentPlanName } from '../services/treatmentPlanNameService';
import { planPatientCount } from '../utils/ganttUtils';
import { getYearPlanMoney, type YearPlanMoney } from '../services/yearPlanPotentialService';
import {
  calculateTemplateSpan,
  calculateTreatmentPlanPlacements,
  type TreatmentPlanRolloutSelection
} from '../services/treatmentPlanRolloutService';
import {
  buildTreatmentPlanFromTemplate,
  buildWeeksFromTemplate,
  getTemplateFirstWeekNumber
} from '../services/treatmentPlanCloneService';
import { setHasUnsavedChanges } from '../store/application/applicationSlice';
import { setPatients, clearPatients } from '../store/patients/patientsSlice';
import {
  setRoomProperties,
  clearRoomProperties
} from '../store/roomProperties/roomPropertiesSlice';
import type { RoomProperty } from '../store/roomProperties/roomPropertiesSlice';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { getPlanLockedMessage } from '../services/scenarioLockErrorService';
import { buildYearPlanErrorDialog } from '../services/yearPlanErrorService';
import { getApiErrorMessage } from '../utils/errorMessageUtils';
import { useAppDispatch } from '../store/hooks';
import { useLocationName } from './useLocationName';
import type { MoveHoverParams } from '../components/GanttChart/GanttCell';

async function fetchRoomProperties(): Promise<RoomProperty[]> {
  const config = createApiConfiguration();
  const roomPropertyApi = new PatientRoomPropertyControllerApi(config);
  const response = await roomPropertyApi.listPatientRoomProperties();
  return response.data.map((rp) => ({
    id: rp.id,
    name: rp.name
  }));
}

export interface ChangeSummary {
  currentTotalPatients: number;
  serverTotalPatients: number;
  // Money the plan would earn, local against server, so the year plan can show
  // what unaccepted edits are worth. Derived here for the same reason the
  // patient totals are: the server cannot price edits it has not been told
  // about.
  currentMoney: YearPlanMoney;
  serverMoney: YearPlanMoney;
  currentTotalPlans: number;
  serverTotalPlans: number;
  treatmentPlansAdded: Record<string, number>;
  treatmentPlansDeleted: Record<string, number>;
  treatmentPlansRenamed: RenamedTreatmentPlan[];
  // A reorder changes no counts and no names, so it needs stating on its own.
  hasReorderedTreatmentPlans: boolean;
}

// Returns a copy of the plan with the capacity applied to the targeted groups
function applyCapacityToPlan(
  plan: TreatmentPlanDto,
  capacity: number,
  targetGroupIds: ReadonlySet<string>
): TreatmentPlanDto {
  return {
    ...plan,
    weeks: plan.weeks.map((w) => ({
      ...w,
      groups: (w.groups ?? []).map((g) =>
        g.id && targetGroupIds.has(g.id) ? { ...g, capacity } : g
      )
    }))
  };
}

// Parses the ?year= query param value; null when absent or not a valid year
function parseYearParam(value: string | null): number | null {
  if (!value) return null;
  const year = Number(value);
  return Number.isInteger(year) && year > 0 ? year : null;
}

function hasYearPlanChanged(
  current: YearPlanResponse | null,
  server: YearPlanResponse | null
): boolean {
  if (current === server) return false;
  if (!current || !server) return current !== server;
  return JSON.stringify(current.treatmentPlans) !== JSON.stringify(server.treatmentPlans);
}

export function useYearPlan(locationId: string | undefined) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // The selected year lives in the URL (?year=) so it survives page refresh
  // and location switches. Absent/invalid param → current calendar year until
  // the initial fetch resolves the scenario year and syncs it back to the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlYear = parseYearParam(searchParams.get(YEAR_QUERY_PARAM));
  const selectedYear = urlYear ?? new Date().getFullYear();

  // Latest-value refs so the fetch effects can read the URL year and write the
  // URL without depending on them (setSearchParams changes identity on every
  // search change, which would otherwise re-trigger the fetch effects). Synced
  // in an effect declared before the fetch effects so they always see current
  // values.
  const urlYearRef = useRef(urlYear);
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => {
    urlYearRef.current = urlYear;
    setSearchParamsRef.current = setSearchParams;
  });

  const writeYearToUrl = useCallback((year: number, options?: { replace?: boolean }): void => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      next.set(YEAR_QUERY_PARAM, String(year));
      return next;
    }, options);
  }, []);

  // User-picked year changes push a history entry; internal syncs (initial
  // scenario resolution, revert on fetch error) replace it instead.
  const setSelectedYear = useCallback(
    (year: number): void => {
      writeYearToUrl(year);
    },
    [writeYearToUrl]
  );

  const [yearPlan, setYearPlanLocal] = useState<YearPlanResponse | null>(null);
  const [treatmentPlanTemplates, setTreatmentPlanTemplates] = useState<
    TreatmentPlanTemplateResponse[]
  >([]);
  // Stores the last server-synced state, used to determine if local edits exist
  const serverYearPlan = useRef<YearPlanResponse | null>(null);
  // The snapshot's money, priced when it arrives rather than read back off the
  // ref while rendering. Same figures either way, but it keeps the changeSummary
  // memo from adding another render-time ref read to the pattern the lint rule
  // already flags there. Written only where the snapshot itself is written.
  const [serverMoney, setServerMoney] = useState<YearPlanMoney>(() => getYearPlanMoney(null));

  // Sets state from a server response — resets the server snapshot and clears unsaved changes state
  const setYearPlanFromServer = useCallback(
    (plan: YearPlanResponse | null) => {
      setYearPlanLocal(plan);
      serverYearPlan.current = plan;
      setServerMoney(getYearPlanMoney(plan));
      dispatch(setHasUnsavedChanges(false));
    },
    [dispatch]
  );

  // Applies a confirmed local edit — compares against server state to track unsaved changes state
  const applyYearPlanEdit = useCallback(
    (plan: YearPlanResponse | null) => {
      setYearPlanLocal(plan);
      dispatch(setHasUnsavedChanges(hasYearPlanChanged(plan, serverYearPlan.current)));
    },
    [dispatch]
  );

  const scenarioYearRef = useRef<number>(selectedYear);
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Read from the shared locations slice rather than fetched here: the header
  // switcher already has the list, and asking for it again just to read one
  // name meant every year-overview load fetched it twice. A list that cannot
  // be loaded still sends the page to the error page — see useLocationName.
  const locationName = useLocationName(locationId);

  // Drop the previous location's scenario the moment the location changes, and do it during render
  // rather than from an effect.
  //
  // `scenarioId` is only ever assigned by a completed fetch, so without this it keeps pointing at
  // the location we just left for as long as the new location's scenario takes to arrive. Everything
  // downstream is keyed on the pair (locationId, scenarioId) — week statuses, the scenario lock, the
  // week calendar — so during that window they ask for a scenario that does not exist in this
  // location. The backend answers 404, and the week-status poll classifies 404 as permanent: it
  // stops polling and opens an error dialog, on every single location switch.
  //
  // An effect would be too late. Those hooks live in the same component, so their effects for the
  // render that introduced the new location have already run — and already fired the mismatched
  // request — by the time an effect here could clear this. Resetting during render re-renders before
  // any of them commit, so they see `undefined` and skip the fetch instead.
  const [renderedLocationId, setRenderedLocationId] = useState(locationId);
  if (locationId !== renderedLocationId) {
    setRenderedLocationId(locationId);
    setScenarioId(null);
  }

  const totalWeeks = yearPlan?.weeks.length ?? 52;
  // Snapshot taken before a move starts, used to restore state on discard
  const yearPlanSnapshot = useRef<YearPlanResponse | null>(null);

  // Initial fetch
  useEffect(() => {
    if (!locationId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const config = createApiConfiguration();
        const scenarioApi = new ScenarioControllerApi(config);
        const yearPlanApi = new YearPlanControllerApi(config);

        // A year in the URL (refresh, location switch) wins over the backend's
        // notion of the current scenario.
        const initialYear = urlYearRef.current;
        const scenarioResponse = await (initialYear === null
          ? scenarioApi.getCurrentScenario({ locationId })
          : scenarioApi.getScenarioForYear({ locationId, year: initialYear }));

        const scenarioId = scenarioResponse.data.id;
        setScenarioId(scenarioId);

        const year = scenarioResponse.data.year;
        scenarioYearRef.current = year;
        if (initialYear !== year) {
          writeYearToUrl(year, { replace: true });
        }

        if (scenarioId) {
          const patientApi = new PatientControllerApi(config);
          const [yearPlanResponse, patientsResponse, roomProps] = await Promise.all([
            yearPlanApi.getYearPlan({ locationId, scenarioId }),
            patientApi.listPatients({ locationId }),
            fetchRoomProperties()
          ]);
          setYearPlanFromServer(yearPlanResponse.data);
          dispatch(setPatients(patientsResponse.data));
          dispatch(setRoomProperties(roomProps));
        }
      } catch (error) {
        console.error('Failed to load year plan on initial fetch:', error);
        navigate(`/${ROUTES.errorPage}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [locationId, setYearPlanFromServer, writeYearToUrl, dispatch, navigate]);

  // Fetch on year change
  useEffect(() => {
    if (!locationId || selectedYear === scenarioYearRef.current) return;

    const fetchYearPlan = async () => {
      setLoading(true);
      try {
        const config = createApiConfiguration();
        const scenarioApi = new ScenarioControllerApi(config);
        const yearPlanApi = new YearPlanControllerApi(config);

        const scenarioResponse = await scenarioApi.getScenarioForYear({
          locationId,
          year: selectedYear
        });

        const fetchedScenarioId = scenarioResponse.data.id;
        setScenarioId(fetchedScenarioId);
        scenarioYearRef.current = scenarioResponse.data.year;

        if (fetchedScenarioId) {
          const patientApi = new PatientControllerApi(config);
          const [yearPlanResponse, patientsResponse, roomProps] = await Promise.all([
            yearPlanApi.getYearPlan({
              locationId,
              scenarioId: fetchedScenarioId
            }),
            patientApi.listPatients({ locationId }),
            fetchRoomProperties()
          ]);
          setYearPlanFromServer(yearPlanResponse.data);
          dispatch(setPatients(patientsResponse.data));
          dispatch(setRoomProperties(roomProps));
        } else {
          setYearPlanFromServer(null);
          dispatch(clearPatients());
          dispatch(clearRoomProperties());
        }
        setTreatmentPlanTemplates([]);
      } catch (error) {
        console.error('Failed to load year plan on year change:', error);
        dispatch(showError({ message: t('fetch-error') }));
        writeYearToUrl(scenarioYearRef.current, { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchYearPlan();
  }, [locationId, selectedYear, setYearPlanFromServer, writeYearToUrl, dispatch, t]);

  // Add treatment plans from a template. Without a selection this drops in a single
  // unplaced plan; with one it rolls the template out as a count of plans separated by
  // a gap in weeks. Plans that would start past the end of the year are cut.
  const handleAddTreatmentPlan = useCallback(
    (template: TreatmentPlanTemplateResponse, selection?: TreatmentPlanRolloutSelection) => {
      if (!yearPlan || !template.id) return;

      const buildPlan = (weeks: TreatmentPlanDto['weeks']): TreatmentPlanDto =>
        buildTreatmentPlanFromTemplate(template, weeks);

      let newPlans: TreatmentPlanDto[] = [buildPlan([])];

      if (selection) {
        const templateWeekNumbers = template.weeks.map((w) => w.weekNumber);
        const minWeek = getTemplateFirstWeekNumber(template);

        newPlans = calculateTreatmentPlanPlacements({
          startWeekNumber: selection.startWeekNumber,
          planCount: selection.planCount,
          weeksBetweenPlans: selection.weeksBetweenPlans,
          planSpan: calculateTemplateSpan(templateWeekNumbers),
          lastWeekNumber: selection.lastWeekNumber
        }).map((placement) =>
          buildPlan(
            buildWeeksFromTemplate(
              template,
              placement.startWeekNumber - minWeek,
              selection.capacity
            )
          )
        );
      }

      // Adding the template as a category (no start week) creates an empty
      // placeholder plan so the Gantt row appears. Once concrete plans are
      // rolled out for that template they keep the row visible, so the
      // placeholder must go — otherwise it survives as a duplicate plan with
      // no weeks pointing at the same template.
      const existingPlans = newPlans.some((p) => p.weeks.length > 0)
        ? yearPlan.treatmentPlans.filter(
            (p) => !(p.treatmentPlanTemplateId === template.id && p.weeks.length === 0)
          )
        : yearPlan.treatmentPlans;

      applyYearPlanEdit({
        ...yearPlan,
        treatmentPlans: [...existingPlans, ...newPlans]
      });
    },
    [yearPlan, applyYearPlanEdit]
  );

  // Adds the plans a simulation proposed, in one edit so they review, save and
  // discard together. Their templates' empty placeholder rows go, for the same
  // reason a roll-out removes them.
  const handleAddSimulatedTreatmentPlans = useCallback(
    (plans: TreatmentPlanDto[]) => {
      if (!yearPlan || plans.length === 0) return;
      const templateIds = new Set(plans.map((p) => p.treatmentPlanTemplateId));
      const existingPlans = yearPlan.treatmentPlans.filter(
        (p) => !(templateIds.has(p.treatmentPlanTemplateId) && p.weeks.length === 0)
      );
      applyYearPlanEdit({ ...yearPlan, treatmentPlans: [...existingPlans, ...plans] });
    },
    [yearPlan, applyYearPlanEdit]
  );

  // Save pre-move state so we can restore on discard
  const handleMoveStart = useCallback(() => {
    yearPlanSnapshot.current = yearPlan;
  }, [yearPlan]);

  // Restore pre-move state without affecting unsaved changes state
  const handleMoveDiscard = useCallback(() => {
    if (yearPlanSnapshot.current) {
      setYearPlanLocal(yearPlanSnapshot.current);
      yearPlanSnapshot.current = null;
    }
  }, []);

  // Confirm the move — now marks the change as a committed edit
  const handleMoveAccept = useCallback(() => {
    if (yearPlanSnapshot.current) {
      applyYearPlanEdit(yearPlan);
      yearPlanSnapshot.current = null;
    }
  }, [yearPlan, applyYearPlanEdit]);

  // Refetch year plan from server
  const refreshYearPlan = useCallback(async (): Promise<string | undefined> => {
    if (!locationId) return;
    const config = createApiConfiguration();
    const scenarioApi = new ScenarioControllerApi(config);
    const yearPlanApi = new YearPlanControllerApi(config);

    const scenarioResponse = await scenarioApi.getScenarioForYear({
      locationId,
      year: selectedYear
    });
    const fetchedScenarioId = scenarioResponse.data.id;
    if (fetchedScenarioId) {
      const patientApi = new PatientControllerApi(config);
      const [yearPlanResponse, patientsResponse] = await Promise.all([
        yearPlanApi.getYearPlan({
          locationId,
          scenarioId: fetchedScenarioId
        }),
        patientApi.listPatients({ locationId })
      ]);
      setYearPlanFromServer(yearPlanResponse.data);
      dispatch(setPatients(patientsResponse.data));
    }
    return fetchedScenarioId;
  }, [locationId, selectedYear, setYearPlanFromServer, dispatch]);

  const fetchTemplates = useCallback(async () => {
    const config = createApiConfiguration();
    const templateApi = new TreatmentPlanTemplateControllerApi(config);
    const response = await templateApi.getTreatmentPlanTemplates();
    setTreatmentPlanTemplates(response.data);
  }, []);

  const clearTemplates = useCallback(() => {
    setTreatmentPlanTemplates([]);
  }, []);

  // Discard all edits: refetch from server, resetting local state
  const handleDiscardAll = useCallback(async () => {
    await refreshYearPlan();
    yearPlanSnapshot.current = null;
  }, [refreshYearPlan]);

  // Save all edits to server, then sync local state with server response
  const handleAcceptAll = useCallback(async () => {
    if (!locationId || !yearPlan?.treatmentPlans) return;
    const config = createApiConfiguration();
    const scenarioApi = new ScenarioControllerApi(config);
    const yearPlanApi = new YearPlanControllerApi(config);

    try {
      const scenarioResponse = await scenarioApi.getScenarioForYear({
        locationId,
        year: selectedYear
      });
      const scenarioId = scenarioResponse.data.id;
      if (scenarioId) {
        // A template with nothing rolled out must never be persisted: empty
        // plans are edit-time placeholders (and possible legacy leftovers), so
        // they are stripped from the payload here.
        const response = await yearPlanApi.updateTreatmentPlans({
          locationId,
          scenarioId,
          updateTreatmentPlansRequest: {
            treatmentPlans: yearPlan.treatmentPlans.filter((p) => p.weeks.length > 0)
          }
        });
        setYearPlanFromServer(response.data);
      }
      yearPlanSnapshot.current = null;
    } catch (error) {
      // The save endpoint is lock-guarded, so this is where another user holding the plan lands —
      // and a lock clash the user cannot attribute to anyone reads as a broken save. Surfaced
      // here rather than on the page so it is unit-testable and cannot drift between callers.
      console.error('Failed to save the year plan', error);
      // A revoked permission (403) keeps its own title and "retrying cannot help" wording; a lock
      // clash names the holder; anything else falls back to the server's message.
      const lockedMessage = getPlanLockedMessage(error, t);
      dispatch(
        showError(
          lockedMessage
            ? { message: lockedMessage }
            : buildYearPlanErrorDialog(
                error,
                t,
                getApiErrorMessage(error, t('error-save-treatment-plans'))
              )
        )
      );
      // The callers hang their flow control off the rejection: the page keeps the lock and the
      // pending edits, and the toolbar stays in edit mode so the edits are still reachable.
      throw error;
    }
  }, [locationId, selectedYear, yearPlan, setYearPlanFromServer, dispatch, t]);

  // Remove a treatment plan from local state (persisted to server on handleAcceptAll)
  const handleDeleteTreatmentPlan = useCallback(
    (treatmentPlanId: string) => {
      if (!yearPlan?.treatmentPlans) return;
      applyYearPlanEdit({
        ...yearPlan,
        treatmentPlans: yearPlan.treatmentPlans.filter((p) => p.id !== treatmentPlanId)
      });
    },
    [yearPlan, applyYearPlanEdit]
  );

  // Remove every treatment plan in a year-view row (persisted to server on handleAcceptAll)
  const handleDeleteTreatmentPlans = useCallback(
    (treatmentPlanIds: string[]) => {
      if (!yearPlan?.treatmentPlans || treatmentPlanIds.length === 0) return;
      const targetIds = new Set(treatmentPlanIds);
      applyYearPlanEdit({
        ...yearPlan,
        treatmentPlans: yearPlan.treatmentPlans.filter((p) => !targetIds.has(p.id))
      });
    },
    [yearPlan, applyYearPlanEdit]
  );

  // Rename rolled-out treatment plans (persisted to server on handleAcceptAll).
  // The name belongs to the row, so every plan in it takes the same name. The
  // template is never touched.
  const handleRenameTreatmentPlans = useCallback(
    (treatmentPlanIds: string[], name: string) => {
      const validName = normalizeTreatmentPlanName(name);
      if (!yearPlan?.treatmentPlans || validName === null || treatmentPlanIds.length === 0) return;
      const targetIds = new Set(treatmentPlanIds);
      applyYearPlanEdit({
        ...yearPlan,
        treatmentPlans: yearPlan.treatmentPlans.map((p) =>
          targetIds.has(p.id) ? { ...p, name: validName } : p
        )
      });
    },
    [yearPlan, applyYearPlanEdit]
  );

  // Move a year-view row one place up or down (persisted to server on
  // handleAcceptAll — the plan array order is the row order). The service hands
  // back the same array when nothing can move, which keeps an edge move from
  // marking the plan dirty.
  const handleMoveTreatmentPlanRow = useCallback(
    (templateId: string, direction: RowMoveDirection) => {
      if (!yearPlan?.treatmentPlans) return;
      const reordered = moveTreatmentPlanRow(yearPlan.treatmentPlans, templateId, direction);
      if (reordered === yearPlan.treatmentPlans) return;
      applyYearPlanEdit({ ...yearPlan, treatmentPlans: reordered });
    },
    [yearPlan, applyYearPlanEdit]
  );

  // Update capacity on the specified intervention groups for a plan (persisted to server on handleAcceptAll)
  const updateCapacity = useCallback(
    (treatmentPlanId: string, capacity: number, targetGroupIds: string[]) => {
      if (!yearPlan?.treatmentPlans || targetGroupIds.length === 0) return;
      const targetIdSet = new Set(targetGroupIds);
      applyYearPlanEdit({
        ...yearPlan,
        treatmentPlans: yearPlan.treatmentPlans.map((p) =>
          p.id === treatmentPlanId ? applyCapacityToPlan(p, capacity, targetIdSet) : p
        )
      });
    },
    [yearPlan, applyYearPlanEdit]
  );

  // Preview a move locally — uses setYearPlanLocal so unsaved changes state is not updated
  // until the move is confirmed via handleMoveAccept
  const handleMoveIntervention = useCallback(
    (params: MoveHoverParams) => {
      if (!yearPlan?.treatmentPlans) return;

      const delta = params.direction === 'left' ? -1 : 1;
      const planIndex = yearPlan.treatmentPlans.findIndex((p) => p.id === params.treatmentPlanId);
      if (planIndex === -1) return;

      const plan = yearPlan.treatmentPlans[planIndex];

      // Build a flat view of weeks containing groups
      type WeekRef = { weekIndex: number; weekNumber: number; groupIds: string[] };
      const weekRefs: WeekRef[] = plan.weeks.map((w, wi) => ({
        weekIndex: wi,
        weekNumber: w.weekNumber ?? 0,
        groupIds: (w.groups ?? []).map((g) => g.id ?? '').filter(Boolean)
      }));

      // Find the week containing the target group
      const targetWeekRef = weekRefs.find((wr) => wr.groupIds.includes(params.groupId));
      if (!targetWeekRef) return;

      // A series move shifts the whole plan, so a plan whose tail ran past the
      // year end can always be pulled back from any block still in view.
      const weeksToMove = params.scope === 'single' ? [targetWeekRef] : weekRefs;

      const weekIndicesToShift = new Set(weeksToMove.map((wr) => wr.weekIndex));
      const updatedWeekNumbers = weekRefs.map((wr) =>
        weekIndicesToShift.has(wr.weekIndex) ? wr.weekNumber + delta : wr.weekNumber
      );
      // A plan may run on past the year's last week, since the following year
      // shows the tail as carried over, but it has to start inside the year.
      const isMoveAllowed =
        weeksToMove.every((wr) => wr.weekNumber + delta >= 1) &&
        Math.min(...updatedWeekNumbers) <= totalWeeks;
      if (!isMoveAllowed) return;

      const updatedWeeks = plan.weeks.map((w, wi) =>
        weekIndicesToShift.has(wi)
          ? { ...w, weekNumber: (w.weekNumber ?? 0) + delta }
          : w
      );

      const updatedPlans = [...yearPlan.treatmentPlans];
      updatedPlans[planIndex] = { ...plan, weeks: updatedWeeks };

      setYearPlanLocal({ ...yearPlan, treatmentPlans: updatedPlans });
    },
    [yearPlan, totalWeeks]
  );

  // Computes a summary of changes between local state and server snapshot.
  // Safe to read serverYearPlan.current here because it only changes via
  // setYearPlanFromServer, which also updates yearPlan (the memo dependency).
  const changeSummary = useMemo((): ChangeSummary | null => {
    const server = serverYearPlan.current;
    if (!yearPlan || !server) return null;

    const currentPlans = yearPlan.treatmentPlans;
    const serverPlans = server.treatmentPlans;

    // Summed over plans, not over groups: two treatment plans are two cohorts,
    // while the groups inside one plan hold the same people.
    const currentTotalPatients = currentPlans.reduce((sum, p) => sum + planPatientCount(p), 0);
    const serverTotalPatients = serverPlans.reduce((sum, p) => sum + planPatientCount(p), 0);

    const serverIds = new Set(serverPlans.map((p) => p.id));
    const currentIds = new Set(currentPlans.map((p) => p.id));

    const treatmentPlansAdded: Record<string, number> = {};
    for (const p of currentPlans.filter((p) => !serverIds.has(p.id))) {
      treatmentPlansAdded[p.name] = (treatmentPlansAdded[p.name] ?? 0) + 1;
    }

    const treatmentPlansDeleted: Record<string, number> = {};
    for (const p of serverPlans.filter((p) => !currentIds.has(p.id))) {
      treatmentPlansDeleted[p.name] = (treatmentPlansDeleted[p.name] ?? 0) + 1;
    }

    return {
      currentTotalPatients,
      serverTotalPatients,
      currentMoney: getYearPlanMoney(yearPlan),
      serverMoney,
      currentTotalPlans: currentPlans.length,
      serverTotalPlans: serverPlans.length,
      treatmentPlansAdded,
      treatmentPlansDeleted,
      treatmentPlansRenamed: collectRenamedTreatmentPlans(currentPlans, serverPlans),
      hasReorderedTreatmentPlans: hasTreatmentPlanOrderChanged(currentPlans, serverPlans)
    };
  }, [yearPlan, serverMoney]);

  return {
    yearPlan,
    changeSummary,
    treatmentPlanTemplates,
    selectedYear,
    setSelectedYear,
    scenarioId,
    locationName,
    loading,
    handleAddTreatmentPlan,
    handleAddSimulatedTreatmentPlans,
    handleDeleteTreatmentPlan,
    handleDeleteTreatmentPlans,
    handleRenameTreatmentPlans,
    handleMoveTreatmentPlanRow,
    updateCapacity,
    handleMoveIntervention,
    handleMoveStart,
    handleMoveDiscard,
    handleMoveAccept,
    handleDiscardAll,
    handleAcceptAll,
    refreshYearPlan,
    fetchTemplates,
    clearTemplates
  };
}
